import { fromZonedTime } from "date-fns-tz";

/** Canonical shift clock: closed whole minutes plus the open active segment.
 * Paused/ended time is never counted. Matches the persisted minute precision.
 */
export const MAX_ACTIVE_SHIFT_MINUTES = 490;
export const AUTO_PAUSE_REASON = "AUTO_PAUSE_MAX_WORK_DURATION";
export const AUTO_PAUSE_DAY_BOUNDARY_REASON = "AUTO_PAUSE_MADRID_DAY_BOUNDARY";
export const SHIFT_END_UNVERIFIED_NOTE_MARKER = "[SHIFT_END_UNVERIFIED]";
export const SHIFT_END_UNVERIFIED_NOTE = `${SHIFT_END_UNVERIFIED_NOTE_MARKER} Ejecución no verificada al cierre de jornada; no se imputaron minutos.`;
export type ShiftClock = { status: string; totalMinutes: number | null; activeStartAt: Date | null; date?: string | Date | null };
export type ShiftControlRecord = { status: string; date: string };
export type AssignmentClock = {
  status: string;
  totalMinutes: number | null;
  activeStartTime?: Date | string | null;
  date?: string | Date | null;
  shiftDate?: string | Date | null;
  shiftStatus?: string | null;
  shiftTotalMinutes?: number | null;
  shiftActiveStartAt?: Date | string | null;
  startTime?: string | null;
  endTime?: string | null;
};

function asDate(value: Date | string | null | undefined): Date | null {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function civilDate(value: string | Date | null | undefined): string | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : madridCivilDate(value);
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

function nextCivilDate(date: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return null;
  const next = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + 1));
  return next.toISOString().slice(0, 10);
}

function madridCivilDayEnd(date: string | Date | null | undefined): Date | null {
  const currentDate = civilDate(date);
  const nextDate = currentDate ? nextCivilDate(currentDate) : null;
  return nextDate ? fromZonedTime(`${nextDate}T00:00:00.000`, "Europe/Madrid") : null;
}

/** The first instant at which this active shift must stop accruing, without persisting a transition. */
export function effectiveShiftCutoffAt(shift: ShiftClock): Date | null {
  if (shift.status !== "active") return null;
  const activeStartAt = asDate(shift.activeStartAt);
  if (!activeStartAt) return null;
  const closedMinutes = Math.max(0, shift.totalMinutes ?? 0);
  const maxDurationCutoff = new Date(activeStartAt.getTime() + Math.max(0, MAX_ACTIVE_SHIFT_MINUTES - closedMinutes) * 60000);
  const dayEnd = madridCivilDayEnd(shift.date);
  return dayEnd && dayEnd < maxDurationCutoff ? dayEnd : maxDurationCutoff;
}

/** Madrid wall-clock time for legacy TIME columns (durations use timestamps). */
export function madridClockTime(at: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Madrid",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).format(at);
}

/** Add only the open activity segment to its already persisted minutes. */
export function effectiveAssignmentMinutes(assignment: AssignmentClock, at: Date): number {
  const closed = Math.max(0, assignment.totalMinutes ?? 0);
  const started = assignment.status === "working" ? asDate(assignment.activeStartTime) : null;
  let stopAt = at;
  const assignmentDayEnd = madridCivilDayEnd(assignment.date ?? assignment.shiftDate);
  if (assignmentDayEnd && assignmentDayEnd < stopAt) stopAt = assignmentDayEnd;
  if (assignment.shiftStatus != null) {
    if (assignment.shiftStatus !== "active") {
      if (started && started < stopAt) stopAt = started;
    } else {
      const shiftCutoff = effectiveShiftCutoffAt({
        status: "active",
        totalMinutes: assignment.shiftTotalMinutes ?? 0,
        activeStartAt: asDate(assignment.shiftActiveStartAt),
        date: assignment.shiftDate ?? assignment.date,
      });
      if (shiftCutoff && shiftCutoff < stopAt) stopAt = shiftCutoff;
      else if (!shiftCutoff && started && started < stopAt) stopAt = started;
    }
  } else if (started) {
    // Sparse legacy/UI callers still receive a finite open segment; DB read
    // models include the associated shift and use its exact remaining budget.
    const fallbackCutoff = new Date(started.getTime() + MAX_ACTIVE_SHIFT_MINUTES * 60000);
    if (fallbackCutoff < stopAt) stopAt = fallbackCutoff;
  }
  const open = started ? Math.max(0, Math.floor((stopAt.getTime() - started.getTime()) / 60000)) : 0;
  return closed + open;
}

export function startAssignmentUpdate(assignment: AssignmentClock, at: Date) {
  return {
    status: "working" as const,
    activeStartTime: at,
    endTime: null,
    ...(!hasReliableDailyStartTime(assignment.startTime) ? { startTime: madridClockTime(at) } : {}),
  };
}

export function pauseAssignmentUpdate(assignment: AssignmentClock, at: Date) {
  const activeStartMarker = asDate(assignment.activeStartTime);
  const hasActiveSegment = assignment.status === "working" && activeStartMarker !== null;
  const hasRecordedMinutes = assignment.totalMinutes !== null && assignment.totalMinutes !== undefined;
  return {
    status: "paused" as const,
    activeStartTime: null,
    // Older master rows may have only activeStartTime. Preserve a durable
    // execution marker before clearing it, including sub-minute pauses.
    ...(activeStartMarker && !hasReliableDailyStartTime(assignment.startTime) ? { startTime: madridClockTime(activeStartMarker) } : {}),
    // Do not turn a legacy row with no duration evidence into an attested zero.
    totalMinutes: hasActiveSegment || hasRecordedMinutes
      ? effectiveAssignmentMinutes(assignment, at)
      : assignment.totalMinutes,
  };
}

/** Ending from paused adds no rest; ending from working adds only its final segment. */
export function completeAssignmentUpdate(assignment: AssignmentClock, at: Date | null) {
  const activeStartMarker = asDate(assignment.activeStartTime);
  const hasActiveSegment = assignment.status === "working" && activeStartMarker !== null;
  const hasRecordedMinutes = assignment.totalMinutes !== null && assignment.totalMinutes !== undefined;
  const hasDurationEvidence = hasActiveSegment || hasRecordedMinutes;
  return {
    status: "completed" as const,
    activeStartTime: null,
    // Preserve unknown legacy duration as unknown; writing 0 would falsely
    // classify it as a measured workday in CAL-05/CAL-09.
    totalMinutes: hasDurationEvidence
      ? at ? effectiveAssignmentMinutes(assignment, at) : Math.max(0, assignment.totalMinutes ?? 0)
      : assignment.totalMinutes,
    ...(activeStartMarker && !hasReliableDailyStartTime(assignment.startTime) ? { startTime: madridClockTime(activeStartMarker) } : {}),
    ...(at && hasDurationEvidence ? { endTime: madridClockTime(at) } : {}),
  };
}

/** Execution evidence, independent of assignment approval. */
export type DailyExecutionRecord = { status: string; approvalStatus?: string | null; activeStartTime?: Date | string | null; startTime?: string | null; totalMinutes?: number | null };

export function hasReliableDailyStartTime(value: string | null | undefined): boolean {
  const match = typeof value === "string" && /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!match) return false;
  const [hours, minutes, seconds] = [Number(match[1]), Number(match[2]), Number(match[3] ?? "0")];
  return hours <= 23 && minutes <= 59 && seconds <= 59;
}

export function isRealDailyExecution(record: DailyExecutionRecord): boolean {
  // Administrative status can change without recording execution. Only the
  // persisted start marker or positive worked minutes prove real activity.
  // startTime is written by startWork (never PM planning) and survives a
  // legitimate sub-minute pause whose whole-minute total remains zero.
  return Boolean(record.activeStartTime) || (record.totalMinutes ?? 0) > 0 || hasReliableDailyStartTime(record.startTime);
}

export type ShiftEndAssignmentRecord = {
  id: number;
  installationId: number;
  status: string;
  activeStartTime: Date | string | null;
  totalMinutes: number | null;
  startTime?: string | null;
  notes?: string | null;
};

function hasValidActiveStart(value: Date | string | null | undefined): boolean {
  if (value == null) return false;
  const date = value instanceof Date ? value : new Date(value);
  return !Number.isNaN(date.getTime());
}

/**
 * In App ODS, startTime is written only by startWork; PM planning inserts no
 * clock values, and legacy recovery derives a missing value from activeStartTime.
 * This writer invariant makes a valid startTime a durable marker even when a
 * legitimate sub-minute pause rounded totalMinutes to zero.
 */
export function hasReliableDailyAssignmentStartMarker(row: ShiftEndAssignmentRecord): boolean {
  if (hasValidActiveStart(row.activeStartTime)) return true;
  if (Number.isFinite(row.totalMinutes) && Number(row.totalMinutes) > 0) return true;
  return hasReliableDailyStartTime(row.startTime);
}

/** Only working/paused rows with persisted evidence of an actual start close. */
export function selectStartedAssignmentsForShiftEnd<T extends ShiftEndAssignmentRecord>(rows: readonly T[]): T[] {
  return rows.filter((row) =>
    (row.status === "working" || row.status === "paused") && hasReliableDailyAssignmentStartMarker(row)
  );
}

/** Started-looking rows without evidence are disarmed, never completed. */
export function selectUnverifiedAssignmentsForShiftEnd<T extends ShiftEndAssignmentRecord>(rows: readonly T[]): T[] {
  return rows.filter((row) =>
    (row.status === "working" || row.status === "paused") && !hasReliableDailyAssignmentStartMarker(row)
  );
}

export function normalizeUnverifiedAssignmentAtShiftEnd(assignment: {
  notes?: string | null;
}) {
  const existingNotes = assignment.notes?.trim() ?? "";
  const notes = existingNotes.includes(SHIFT_END_UNVERIFIED_NOTE_MARKER)
    ? existingNotes
    : [existingNotes, SHIFT_END_UNVERIFIED_NOTE].filter(Boolean).join("\n");
  return { status: "assigned" as const, activeStartTime: null, notes };
}

/** Explicit PM replanning clears only the technical close marker; other notes and audit history remain. */
export function clearShiftEndUnverifiedNote(notes?: string | null): string | null {
  if (!notes || !notes.includes(SHIFT_END_UNVERIFIED_NOTE_MARKER)) return notes ?? null;
  const preservedNotes = notes.split(/\r?\n/)
    .filter((line) => !line.includes(SHIFT_END_UNVERIFIED_NOTE_MARKER))
    .join("\n");
  return preservedNotes || null;
}

export type DailyReportActivityRow = DailyExecutionRecord & {
  date: string;
  installationId: number;
  installationName: string;
  totalMinutes: number | null;
  activeStartTime?: Date | string | null;
  startTime?: string | null;
  shiftDate?: string | Date | null;
  shiftStatus?: string | null;
  shiftTotalMinutes?: number | null;
  shiftActiveStartAt?: Date | string | null;
};
export type DailyReportCoverageActivityRow = DailyReportActivityRow & { hasNoSharedReport: boolean };

export function buildSwitchReasonNote(reason: string): string {
  return `Cambio de actividad: ${reason}`;
}

export function mergeSwitchReasonIntoNotes(existingNotes: string | null | undefined, reason: string): string {
  const reasonNote = buildSwitchReasonNote(reason);
  return existingNotes && existingNotes.trim().length > 0 ? `${existingNotes}\n${reasonNote}` : reasonNote;
}

/** Sum real assignment minutes once per installation and Madrid civil day.
 * When now is provided, include the open working segment without mutating stored minutes.
 */
export function aggregateDailyReportActivity(rows: DailyReportActivityRow[], now?: Date) {
  const totals = new Map<string, {
    date: string;
    installationId: number;
    installationName: string;
    knownMinutes: number;
    hasUnknownDuration: boolean;
  }>();
  for (const row of rows) {
    if (!isRealDailyExecution(row)) continue;
    const key = `${row.installationId}:${row.date}`;
    const current = totals.get(key) ?? {
      date: row.date,
      installationId: row.installationId,
      installationName: row.installationName,
      knownMinutes: 0,
      hasUnknownDuration: false,
    };
    // A valid startTime is execution evidence, not duration evidence. Only
    // derive an open segment from a valid activeStartTime marker; otherwise
    // preserve the persisted total, including null for unknown duration.
    const rowMinutes = now && row.status === "working" && asDate(row.activeStartTime)
      ? effectiveAssignmentMinutes(row, now)
      : row.totalMinutes;
    if (rowMinutes == null || !Number.isFinite(rowMinutes)) {
      current.hasUnknownDuration = true;
    } else {
      current.knownMinutes += Math.max(0, rowMinutes);
    }
    totals.set(key, current);
  }
  return Array.from(totals.values())
    .map(({ knownMinutes, hasUnknownDuration, ...row }) => ({
      ...row,
      totalMinutes: hasUnknownDuration ? null : knownMinutes,
    }))
    .sort((a, b) => a.date.localeCompare(b.date) || a.installationId - b.installationId);
}

/** Whether any installation has reportable real work not covered by a shared part. */
export function hasOutstandingDailyReportCoverage(rows: DailyReportCoverageActivityRow[], now?: Date): boolean {
  return aggregateDailyReportActivity(rows.filter((row) => row.hasNoSharedReport), now).length > 0;
}

/** The displayed shift is today's record, or an actually active older record.
 * An older paused/ended shift is a report obligation, not today's clock.
 */
export function selectShiftForControl<T extends ShiftControlRecord>(todayShift: T | null | undefined, activeShift: T | null | undefined): T | null {
  return todayShift ?? activeShift ?? null;
}

/** Keep a second active clock impossible while allowing prior paused shifts. */
export function shiftStartBlock(todayShift: ShiftControlRecord | null | undefined, activeShift: ShiftControlRecord | null | undefined): "shift_exists_today" | "active_elsewhere" | null {
  if (todayShift) return "shift_exists_today";
  if (activeShift) return "active_elsewhere";
  return null;
}

/** A missing report remains an obligation, but never holds the labor clock open. */
export function effectiveShiftMinutes(shift: ShiftClock, now: Date): number {
  const closed = Math.max(0, shift.totalMinutes ?? 0);
  if (shift.status !== "active") return closed;
  const activeStartAt = asDate(shift.activeStartAt);
  if (!activeStartAt) return Math.min(MAX_ACTIVE_SHIFT_MINUTES, closed);
  const cutoff = effectiveShiftCutoffAt(shift);
  const stopAt = cutoff && cutoff < now ? cutoff : now;
  const open = Math.max(0, Math.floor((stopAt.getTime() - activeStartAt.getTime()) / 60000));
  return Math.min(MAX_ACTIVE_SHIFT_MINUTES, closed + open);
}
/** Exact threshold timestamp, not the later housekeeping execution timestamp.
 * Only active shifts transition; paused/ended history is untouched.
 */
export function shiftAutoPauseAt(shift: ShiftClock, now: Date): Date | null {
  if (shift.status !== "active") return null;
  const activeStartAt = asDate(shift.activeStartAt);
  const closedMinutes = Math.max(0, shift.totalMinutes ?? 0);
  // Preserve the fail-safe for legacy/corrupt active rows: without a segment
  // start, an already-exhausted global budget must still be reconciled.
  if (!activeStartAt) return closedMinutes >= MAX_ACTIVE_SHIFT_MINUTES ? now : null;
  const cutoff = new Date(activeStartAt.getTime() + Math.max(0, MAX_ACTIVE_SHIFT_MINUTES - closedMinutes) * 60000);
  return cutoff && now >= cutoff ? cutoff : null;
}

/** Persist the labor stop at the exact cutoff even when reconciliation is late. */
export function autoPauseShiftUpdate(shift: ShiftClock, now: Date) {
  const endAt = shiftAutoPauseAt(shift, now);
  if (!endAt) return null;
  return {
    status: "paused" as const,
    activeStartAt: null,
    totalMinutes: MAX_ACTIVE_SHIFT_MINUTES,
    endAt,
    updatedAt: now,
  };
}

/** Stop a still-active prior-day shift at Madrid midnight. A delayed scheduler
 * still uses the exact 490-minute cutoff when that was reached first. */
export function abandonedPriorDayShiftUpdate(
  shift: ShiftClock & { date: string },
  now: Date,
): { status: "paused"; activeStartAt: null; totalMinutes: number; endAt: Date; updatedAt: Date } | null {
  if (shift.status !== "active" || shift.date >= madridCivilDate(now)) return null;

  const cap = shiftAutoPauseAt(shift, now);
  const boundary = madridCivilDayEnd(shift.date);
  if (!boundary) return null;
  // Reconcile at the first effective cutoff. The scheduler can run after
  // both; a later 490-minute cutoff must not extend work past Madrid midnight.
  const endAt = cap && cap.getTime() < boundary.getTime() ? cap : boundary;
  const segmentStart = asDate(shift.activeStartAt);
  const elapsed = segmentStart && endAt > segmentStart
    ? Math.floor((endAt.getTime() - segmentStart.getTime()) / 60000)
    : 0;
  return {
    status: "paused",
    activeStartAt: null,
    totalMinutes: Math.min(MAX_ACTIVE_SHIFT_MINUTES, Math.max(0, shift.totalMinutes ?? 0) + elapsed),
    endAt,
    updatedAt: now,
  };
}

/** Resuming starts a new active segment; the earlier pause is not shift end. */
export function resumeShiftUpdate(now: Date) {
  return { status: "active" as const, activeStartAt: now, endAt: null };
}

/** Explicit close preserves an already-recorded work stop (e.g. 490 min cap). */
export function shiftEndTimestamp(currentEndAt: Date | null, now: Date) {
  return currentEndAt ?? now;
}

export function closePausedShiftUpdate(endAt: Date | null, now: Date) {
  return { status: "ended" as const, endAt, updatedAt: now };
}

/** Recover exact legacy auto-cutoff from its same-transaction system notice. */
export function autoPauseTimestampFromNotification(shiftId: number, message: string | null | undefined) {
  const marker = `${AUTO_PAUSE_REASON}: jornada ${shiftId}, pausa efectiva `;
  if (!message?.startsWith(marker)) return null;
  const match = message.slice(marker.length).match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z)/);
  if (!match) return null;
  const timestamp = new Date(match[1]);
  return Number.isNaN(timestamp.getTime()) ? null : timestamp;
}

/** Accept only a real YYYY-MM-DD date; never infer or alter reported hours. */
export function dailyReportDatePrefill(requestedDate: string | null, today: string) {
  if (!requestedDate || !/^\d{4}-\d{2}-\d{2}$/.test(requestedDate)) return today;
  const parsed = new Date(`${requestedDate}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return today;
  return parsed.toISOString().slice(0, 10) === requestedDate ? requestedDate : today;
}

/** Europe/Madrid civil date from an injected instant, independent of host timezone. */
export function madridCivilDate(now: Date): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid" }).format(now);
}

/** Date objects are instants; date strings already represent a civil date. */
export function dailyAssignmentLookupDate(date: Date | string): string {
  return date instanceof Date ? madridCivilDate(date) : date;
}
